import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { FulfillmentAdminClient, ReservationHolder } from '@/infrastructure/FulfillmentAdminClient';
import { ReservedHoldersModal } from '@/pages/fulfillment/inventory/components/ReservedHoldersModal';
import { orderSkus, readSkuStock, type SkuStock } from '@/pages/fulfillment/viewOrders/lineStock';
import '@/pages/fulfillment/inventory/inventory.css';

export const LINE_STOCK_TITLE =
  'On hand: every unit of this SKU on the book, all shelves. Reserved: what order lines hold on shelves, this one ' +
  'included. Available: on hand less reserved.';

/** A read that failed says nothing about the stock, so it has its own state and never renders as 0. */
export type LineStockRead =
  | { status: 'loading' }
  | { status: 'failed' }
  | { status: 'ready'; bySku: Record<string, SkuStock> };

type HeldRead = { key: string; read: LineStockRead };

/**
 * The stock of an order's skus, all three figures from one inventory read so On hand less Reserved is Available on
 * every line. It reads again whenever the lines reload, keeping the last figures on screen until the new ones land.
 */
export const useLineStock = (
  client: Pick<FulfillmentAdminClient, 'listInventory'>,
  lines: ReadonlyArray<{ sku: string | null | undefined }> | undefined,
): { read: LineStockRead; retry: () => void } => {
  const skus = useMemo(() => orderSkus(lines ?? []), [lines]);
  const key = skus.join(',');
  const [held, setHeld] = useState<HeldRead>({ key: '', read: { status: 'loading' } });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (skus.length === 0) {
      return;
    }
    let current = true;
    readSkuStock(client, skus)
      .then((bySku) => {
        if (current) {
          setHeld({ key, read: { status: 'ready', bySku } });
        }
      })
      .catch(() => {
        if (current) {
          setHeld({ key, read: { status: 'failed' } });
        }
      });
    return () => {
      current = false;
    };
    // `skus` is rebuilt each time the lines reload, which is what asks for fresh figures after a change on the order.
  }, [client, skus, key, attempt]);

  const retry = useCallback(() => {
    setHeld((before) => ({ key: before.key, read: { status: 'loading' } }));
    setAttempt((count) => count + 1);
  }, []);

  // Another order's figures are not this order's: until its own read lands the cells say they are loading.
  return { read: held.key === key ? held.read : { status: 'loading' }, retry };
};

interface LineStockCellProps {
  sku: string | null | undefined;
  read: LineStockRead;
  onRetry: () => void;
  onShowReserving: (sku: string) => void;
}

/** One line's stock, the order desk's three figures stacked in the Stock column (SSD-7847). */
export const LineStockCell = ({ sku, read, onRetry, onShowReserving }: LineStockCellProps) => {
  const code = (sku ?? '').trim();
  if (code === '') {
    return <span className="line-stock-note">–</span>;
  }
  if (read.status === 'loading') {
    return <span className="line-stock-note">Loading…</span>;
  }
  if (read.status === 'failed') {
    return (
      <span className="line-stock-note">
        Stock unavailable
        <button
          type="button"
          className="btn btn-line btn-sm"
          aria-label={`Retry reading the stock of ${code}`}
          onClick={onRetry}
        >
          Retry
        </button>
      </span>
    );
  }
  const stock = read.bySku[code] ?? { onHand: 0, reserved: 0, available: 0 };
  return (
    <div className="line-stock" data-testid="line-stock" title={LINE_STOCK_TITLE}>
      <div>
        <span className="k">On hand</span>
        <span className="v">{stock.onHand}</span>
      </div>
      <div>
        <span className="k">Reserved</span>
        {stock.reserved > 0 ? (
          <button
            type="button"
            className="v reslink"
            aria-label={`Reserved ${stock.reserved}: show the orders holding ${code}`}
            onClick={() => onShowReserving(code)}
          >
            {stock.reserved}
          </button>
        ) : (
          <span className="v">{stock.reserved}</span>
        )}
      </div>
      <div className={stock.available <= 0 ? 'is-short' : undefined}>
        <span className="k">Available</span>
        <span className="v">{stock.available}</span>
      </div>
    </div>
  );
};

/** Who holds a sku's stock, the dialog Stock History opens from its Reserved figure, in its own screen's styling. */
export const useReservingOrders = (
  client: Pick<FulfillmentAdminClient, 'listReservationHolders'>,
  orderId: number | undefined,
) => {
  const [sku, setSku] = useState<string | null>(null);
  const [holders, setHolders] = useState<ReservationHolder[]>([]);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const request = useRef(0);

  const show = useCallback(
    async (target: string) => {
      const mine = ++request.current;
      setSku(target);
      setHolders([]);
      setFailed(false);
      setLoading(true);
      try {
        const result = await client.listReservationHolders(target);
        if (mine === request.current) {
          setHolders(result.holders);
        }
      } catch {
        if (mine === request.current) {
          setFailed(true);
        }
      } finally {
        if (mine === request.current) {
          setLoading(false);
        }
      }
    },
    [client],
  );

  const close = useCallback(() => {
    request.current += 1;
    setSku(null);
  }, []);

  // A holder's row links to its order: following it lands on this same page with another order, so the dialog closes.
  useEffect(close, [close, orderId]);

  const dialog =
    sku === null ? null : (
      // The dialog's rules live under the Inventory screen's classes; `contents` gives the wrapper no box of its own.
      <div className="wms-wireframe ff-ultimate wms-inventory" style={{ display: 'contents' }}>
        <ReservedHoldersModal
          sku={sku}
          holders={holders}
          loading={loading}
          failed={failed}
          onRetry={() => void show(sku)}
          onClose={close}
        />
      </div>
    );

  return { show, dialog };
};
